import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskLines } from "./Reveal";
import { HeroConsole } from "./HeroConsole";
import { PipelineStrip } from "./PipelineStrip";
import { scrollToId } from "../../lib/smooth";
import { useContent } from "../../lib/content";

const fade = (d) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, delay: d, ease: [0.22, 1, 0.36, 1] } });

export const Hero = () => {
  const h = useContent().home;
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden noise pt-28 sm:pt-32 lg:pt-36 pb-10">
      <div className="absolute inset-0 grid-bg [mask-image:radial-gradient(ellipse_at_60%_30%,black,transparent_75%)]" />
      <div className="absolute -top-40 right-[-10%] w-[640px] h-[640px] rounded-full bg-[#3b6ef0]/20 blur-[140px]" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[520px] h-[520px] rounded-full bg-[#ff5500]/10 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.15fr,0.85fr] gap-10 lg:gap-6 items-center">
        <div>
          <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full border hairline bg-white/[0.03] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4fc3f7] blink" />{h.hero_eyebrow}
          </motion.div>
          <h1 data-testid="hero-title" className="font-display font-extrabold text-white mt-7 text-[2.5rem] leading-[0.98] sm:text-6xl lg:text-[4.1rem] xl:text-[4.6rem]">
            <MaskLines onLoad text={h.hero_title} delay={0.2}
              lineClass={(i, n) => (i === n - 1 ? "text-heat-grad italic pr-2" : "")} />
          </h1>
          <motion.p {...fade(0.75)} data-testid="hero-subtitle" className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
            {h.hero_subtitle}
          </motion.p>
          <motion.div {...fade(0.9)} className="mt-9 flex flex-col sm:flex-row gap-3">
            <button data-testid="hero-cta-programma" onClick={() => scrollToId("programma")}
              className="group inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-[#ff5500] hover:bg-[#ff6a1f] text-white font-semibold shadow-[0_10px_40px_-10px_rgba(255,85,0,0.7)] transition-[background-color,transform] active:scale-[0.98]">
              {h.hero_cta_primary}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-cta-consulenza" onClick={() => scrollToId("consulenza")}
              className="inline-flex items-center justify-center h-14 px-7 rounded-full border border-white/15 hover:border-[#4fc3f7]/60 hover:bg-[#4fc3f7]/5 text-white font-medium transition-colors">
              {h.hero_cta_secondary}
            </button>
          </motion.div>
          <motion.p {...fade(1.05)} className="mt-8 max-w-md text-sm text-slate-400 border-l-2 border-[#ff5500] pl-4 italic">
            “{h.hero_claim}”
          </motion.p>
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
          <HeroConsole />
        </motion.div>
      </div>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 mt-10 lg:mt-14">
        <PipelineStrip />
        <button onClick={() => scrollToId("programma")} data-testid="hero-scroll-indicator"
          className="hidden lg:flex mx-auto mt-10 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-white transition-colors">
          <ArrowDown className="w-3.5 h-3.5 animate-bounce" />Scorri
        </button>
      </div>
    </section>
  );
};
